import { useQuery } from "@apollo/client";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import DocumentationButton from "components/DocumentationButton";
import { EmptystatePrColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PageWrapper from "components/PageWrapper";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Typography from "ds/components/Typography";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Module } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import { ModuleContext } from "../Context";
import { GET_MODULE_PULL_REQUESTS } from "./gql";
import PullRequest from "./PullRequest";
import styles from "./styles.module.css";

const ModulePullRequests = () => {
  const { module } = useTypedContext(ModuleContext);
  const { loading, data, error, refetch } = useQuery<{ module: Module }>(GET_MODULE_PULL_REQUESTS, {
    variables: { id: module.id },
    // APOLLO CLIENT UPDATE
  });

  useTitle(`Pull Requests · ${module.id}`);

  useBreadcrumbs([
    {
      title: "Modules",
      link: "/modules",
    },
    {
      title: module.id,
    },
  ]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if ((loading && !data) || !data?.module) {
    return <PageLoading />;
  }

  const { pullRequests } = data.module;

  return (
    <>
      <PageInfoHeader title="Pull requests">
        <Button
          onPress={() => refetch()}
          disabled={loading}
          loading={!!(loading && data?.module)}
          variant="primary"
        >
          Refresh
        </Button>
      </PageInfoHeader>
      <PageWrapper>
        {!!pullRequests &&
          pullRequests.map((pullRequest) => (
            <PullRequest
              key={pullRequest.id}
              moduleApiHost={module.apiHost}
              moduleId={module.id}
              moduleNamespace={module.namespace}
              moduleRepository={module.repository}
              moduleRepositoryURL={module.repositoryURL}
              provider={module.provider}
              pullRequest={pullRequest}
            />
          ))}

        {pullRequests?.length === 0 && (
          <EmptyState
            title="No pull requests yet"
            icon={EmptystatePrColored}
            caption={
              <>
                This is a list of Pull Requests currently open against this module's tracked branch
                on {HUMANIZE_PROVIDER[module.provider]}.<br />
                If something seems missing, it may be because of two reasons:
                <br className={styles.beforeReasons} />
                1) The Pull Request was opened before the creation of this module, or it may be
                targeting a different branch.
                <br />
                2) You do not have a{" "}
                <Typography tag="span" variant="s-body3">
                  .spacelift/config.yml
                </Typography>{" "}
                file that contains at least one test case.
              </>
            }
          >
            <DocumentationButton
              to={getDocsUrl("/vendors/terraform/module-registry#git-repository-structure")}
            />
          </EmptyState>
        )}
      </PageWrapper>
    </>
  );
};

export default ModulePullRequests;
